/**
 * paginateToScreen.js — turn a chapter's reading blocks into SCREEN pages that each fit the
 * viewport exactly (founder ruling 2026-08-10, option A: "real pages — each page exactly fills the
 * screen, pager flips them, text never scrolls or hides").
 *
 * WHY THIS EXISTS. The reveal used the PRINT render's own pagination, so one "page" of an 8.5x11
 * sheet reflowed to ~3 phone screens: the pager said "Page 1 of 4" while the reader scrolled a wall
 * of text, and the pinned dock floated over the middle of a paragraph. Page metaphor, scroll
 * behaviour and overlay were three models fighting each other. This module supplies the missing
 * one: pages sized to the screen actually reading them.
 *
 * WHAT IS PRESERVED, AND WHAT DELIBERATELY IS NOT. The WORDS still derive from the real
 * RemnantBookPDF render — `revealPageText.buildReadingModel` over the pdfjs text layer, gated by
 * `revealReadingCardParity.test.js`. What no longer derives from print is the SPLITTING, and that
 * is the ruling itself: these are screen pages, and the printed book keeps its own pagination
 * untouched. The bridge between the two claims is this module's fidelity property —
 * re-pagination never gains, loses or reorders a word — which `paginateToScreen.test.js` asserts
 * directly, including through splits inside a paragraph.
 *
 * MEASUREMENT IS INJECTED, never estimated. The caller passes a `measure(blocks) -> px` that reads
 * the height off the REAL rendered card, so there is no second copy of the typography to drift from
 * the first. A character-width estimate would be a parallel model of the very thing being measured,
 * which is the shape this repo keeps getting burned by.
 *
 * Pure and dependency-free, so it unit-tests without a DOM (RevealPages itself cannot load in CRA
 * Jest — react-pdf ESM). See .claude/rules/app-multidomain.md.
 */

/** Guard against a pathological measurer looping forever; far above any real chapter. */
const MAX_PAGES = 5000;

/**
 * Fewest words worth stranding at the foot of a page when splitting a paragraph to fill it.
 * Roughly two lines on a phone. Below this the paragraph moves on whole instead: a two-word stub
 * under a full paragraph looks like a mistake, while a third of a blank page looks like one too —
 * this constant is where those two ugly failures are traded off.
 */
const MIN_SPLIT_WORDS = 12;

/**
 * @param {object} args
 * @param {Array<{kind: string, text: string}>} args.blocks  reading blocks, in reading order
 * @param {(blocks: Array) => number} args.measure  height in px of a candidate page's blocks
 * @param {number} args.pageHeight  the height one page may occupy
 * @returns {{ pages: Array<Array<object>> }}  blocks grouped into screen pages
 *
 * Split paragraphs carry `continues` (this page's copy runs on) and `continued` (this page's copy
 * started earlier), so the renderer can suppress a bold opener or a paragraph indent on a
 * continuation rather than pretending a new paragraph began.
 */
export function paginateBlocks({ blocks, measure, pageHeight, minSplitWords = MIN_SPLIT_WORDS }) {
  const all = (blocks || []).filter((b) => b && typeof b.text === 'string' && b.text.trim());
  if (!all.length) return { pages: [] };

  // FAIL SOFT, never guess: with no measurer or no real height, hand back one page holding
  // everything. That is today's scrolling card — worse than paged, but never a lost word.
  const usable = typeof measure === 'function' && Number.isFinite(pageHeight) && pageHeight > 0;
  if (!usable) return { pages: [all] };

  const pages = [];
  const queue = all.slice();
  let guard = 0;

  while (queue.length && guard++ < MAX_PAGES) {
    const page = [];
    while (queue.length) {
      const next = queue[0];
      if (measure(page.concat([next])) <= pageHeight) {
        page.push(queue.shift());
        continue;
      }
      // The whole block will not fit. FILL THE PAGE by splitting it — that is what a book does,
      // and the alternative (breaking here) leaves a third of the page blank because the next
      // paragraph was one line too long, which is exactly how the first build read on a phone.
      const { head, tail } = splitToFit(next, measure, pageHeight, page);
      const worthSplitting = head && head.text.split(/\s+/).length >= minSplitWords;

      // A page may only END on body text. While it holds nothing but a kicker/title, breaking here
      // would strand the chapter heading alone with the story starting overleaf — so an unworthwhile
      // split still beats a break when there is no body on the page yet.
      const canBreak = page.some((b) => b.kind === 'body');

      if (head && (worthSplitting || !canBreak)) {
        page.push(head);
        if (tail) queue[0] = tail; else queue.shift();
        break;
      }
      if (canBreak) break; // only a stub would have fitted: let the paragraph start the next page
      // Not even one word fits (a viewport shorter than a line). Take the block whole rather than
      // spin: an overflowing page is recoverable, a hung render is not.
      page.push(queue.shift());
      break;
    }
    if (!page.length) page.push(queue.shift()); // belt and braces: always consume something
    pages.push(page);
  }

  // The guard tripped. Never drop the remainder — hand it back as a final page.
  if (queue.length) pages.push(queue.splice(0));

  return { pages };
}

/**
 * Largest prefix of `block`'s words that still fits after `prefix`, by binary search on the real
 * measurer. Returns `{ head: null, tail: block }` when nothing fits.
 */
function splitToFit(block, measure, pageHeight, prefix) {
  const words = block.text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return { head: null, tail: block };

  let lo = 1;
  let hi = words.length - 1;
  let best = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const candidate = { ...block, text: words.slice(0, mid).join(' ') };
    if (measure(prefix.concat([candidate])) <= pageHeight) { best = mid; lo = mid + 1; }
    else hi = mid - 1;
  }
  if (!best) return { head: null, tail: block };

  return {
    // The spreads carry any inherited continues/continued from an earlier split of the same
    // paragraph, so a paragraph spanning three pages is marked correctly on all three.
    head: { ...block, text: words.slice(0, best).join(' '), continues: true },
    tail: { ...block, text: words.slice(best).join(' '), continued: true },
  };
}
